import type { FC } from "react";
import React from "react";
import { Box, Flex, StickyBox } from "@edreamsodigeo/prisma-design-system";
import { createFeature } from "@edreamsodigeo/frontend-core";

import type {
  EqualProminenceMobileProps,
  HandleOnContinueWithFlightProps,
} from "./contract";
import {
  NormalPriceSelectionBox,
  PrimePriceSelectionBox,
} from "./components/PriceSelectionBox";
import PriceSelectionBoxTrackingProvider from "./components/PriceSelectionBox/PriceSelectionBoxTrackingProvider";
import FreeTrialBanner from "./components/FreeTrialBanner/FreeTrialBanner";
import EqualProminenceMobileSkeleton from "./components/common/Skeleton/EqualProminenceMobileSkeleton";
import usePersistedFlightSelectionData from "~/clientState/sessionStorage/persistedFlightSelectionData";
import {
  EQUAL_PROMINENCE_FLIGHT_SUMMARY_MOBILE,
  DUALDISPLAY_META_V2,
} from "~/clientState/ab/teams/titans";
import { EQUAL_PROMINENCE_MOBILE } from "~/clientState/ab/teams/primo";
import useTrackPriceEvent from "~/clientState/hooks/useTrackPriceEvent";
import useTrackingOnloadEvent from "~/clientState/hooks/useTrackOnloadEvent";
import { DualPriceStickyBar } from "~/components/engage/DualPriceStickyBar";
import { useIsMeta } from "~/clientState/providers/SessionScopeProvider/SessionScopeProvider";

const EqualProminenceMobileComponent: FC<EqualProminenceMobileProps> = ({
  onContinueWithFlight,
  itineraryData,
  pricePerPassenger: discountedPricePerPassenger,
  nonDiscountedPricePerPassenger,
  loading,
  isFlightSummary = false,
  trackedPrice,
}) => {
  const [prevFlightSelectionDataState, setFlightSelectionData] =
    usePersistedFlightSelectionData();

  useTrackPriceEvent({
    isFlightSummary,
    nonDiscountedPrice: trackedPrice,
  });

  useTrackingOnloadEvent({
    discountedPricePerPassenger,
    nonDiscountedPricePerPassenger,
    isFlightSummary,
    trackingParams: {
      label: "continue-button:onload_pag:summary",
      category: "flights_summary",
      action: "continue_button",
    },
  });

  const handleOnContinueWithFlight = ({
    priceWithoutDiscountSelected,
  }: HandleOnContinueWithFlightProps): void => {
    setFlightSelectionData({
      ...prevFlightSelectionDataState,
      data: {
        ...prevFlightSelectionDataState?.data,
        priceWithoutDiscountSelected,
      },
      showQuickAddPrime: priceWithoutDiscountSelected,
    });
    onContinueWithFlight();
  };

  if (loading) {
    return <EqualProminenceMobileSkeleton />;
  }

  if (
    !discountedPricePerPassenger ||
    !nonDiscountedPricePerPassenger ||
    !itineraryData
  ) {
    return null;
  }

  return (
    <StickyBox to={"bottom"}>
      <Flex
        flexDirection={"column"}
        width={"100%"}
        borderTop={"1px"}
        borderTopColor={"neutral.40"}
      >
        <PriceSelectionBoxTrackingProvider itineraryData={itineraryData}>
          <Box borderTop={1} borderBottom={1} borderColor={"neutral.40"}>
            <NormalPriceSelectionBox
              nonDiscountedPrice={nonDiscountedPricePerPassenger}
              onContinueWithFlight={() =>
                handleOnContinueWithFlight({
                  priceWithoutDiscountSelected: true,
                })
              }
            />
          </Box>
          <PrimePriceSelectionBox
            nonDiscountedPrice={nonDiscountedPricePerPassenger}
            discountedPrice={discountedPricePerPassenger}
            onContinueWithFlight={() =>
              handleOnContinueWithFlight({
                priceWithoutDiscountSelected: false,
              })
            }
          />
        </PriceSelectionBoxTrackingProvider>
        <FreeTrialBanner
          nonDiscountedPrice={nonDiscountedPricePerPassenger}
          discountedPrice={discountedPricePerPassenger}
        />
      </Flex>
    </StickyBox>
  );
};

const MetaDualPriceStickyBar: FC<EqualProminenceMobileProps> = (args) => {
  const isMeta = useIsMeta();

  return isMeta ? (
    <DualPriceStickyBar {...args} />
  ) : (
    <EqualProminenceMobileDualPriceStickyBar {...args} />
  );
};

const DualPriceStickyBarForNonMeta: FC<EqualProminenceMobileProps> = (args) => {
  const isMeta = useIsMeta();

  return isMeta ? (
    <EqualProminenceMobileForFlightSummary {...args} />
  ) : (
    <DualPriceStickyBar {...args} />
  );
};

const EqualProminenceMobileForFlightSummary: FC<EqualProminenceMobileProps> = (
  args,
) => {
  return <EqualProminenceMobileComponent {...args} isFlightSummary />;
};

const EqualProminenceMobileDualPriceStickyBar = createFeature({
  name: EQUAL_PROMINENCE_MOBILE,
  variations: [
    EqualProminenceMobileForFlightSummary,
    EqualProminenceMobileForFlightSummary,
    DualPriceStickyBarForNonMeta,
  ],
  version: 1,
});

const EqualProminenceMobile = createFeature({
  name: EQUAL_PROMINENCE_FLIGHT_SUMMARY_MOBILE,
  variations: [
    EqualProminenceMobileComponent,
    EqualProminenceMobileDualPriceStickyBar,
  ],
  version: 1,
});

const EqualProminenceMobileV2 = createFeature({
  name: DUALDISPLAY_META_V2,
  variations: [EqualProminenceMobile, MetaDualPriceStickyBar],
  version: 1,
});

export default EqualProminenceMobileV2;
